import apiClient from './client';
import type {
  MailchimpGrowth,
  MailchimpDailyGrowth,
  MailchimpHwSaasSplit,
  MailchimpEngagement,
  MailchimpCampaigns,
} from '../types/mailchimp';

/**
 * API client for the Mailchimp analytics endpoints.
 *
 * All backend handlers cache aggressively because the upstream Mailchimp
 * API is rate-limited and slow on bulk operations. Pass `{ refresh: true }`
 * from the UI's manual refresh button to force a fresh fetch on the
 * server side.
 */

const BASE = '/admin/mailchimp';

interface FetchOptions {
  refresh?: boolean;
}

function buildParams(
  extra: Record<string, string | number> = {},
  opts?: FetchOptions
): Record<string, string | number> {
  const params: Record<string, string | number> = { ...extra };
  if (opts?.refresh) params.refresh = 'true';
  return params;
}

export async function getMailchimpGrowth(
  monthsBack: number = 12,
  opts?: FetchOptions
): Promise<MailchimpGrowth> {
  const { data } = await apiClient.get<MailchimpGrowth>(`${BASE}/growth/`, {
    params: buildParams({ months: monthsBack }, opts),
  });
  return data;
}

export async function getMailchimpDailyGrowth(
  days: number = 60,
  opts?: FetchOptions
): Promise<MailchimpDailyGrowth> {
  const { data } = await apiClient.get<MailchimpDailyGrowth>(
    `${BASE}/daily-growth/`,
    { params: buildParams({ days }, opts) }
  );
  return data;
}

export async function getMailchimpHwSaasSplit(
  opts?: FetchOptions
): Promise<MailchimpHwSaasSplit> {
  const { data } = await apiClient.get<MailchimpHwSaasSplit>(
    `${BASE}/hw-saas-split/`,
    { params: buildParams({}, opts) }
  );
  return data;
}

export async function getMailchimpEngagement(
  days: number = 30,
  opts?: FetchOptions
): Promise<MailchimpEngagement> {
  const { data } = await apiClient.get<MailchimpEngagement>(
    `${BASE}/engagement/`,
    { params: buildParams({ days }, opts) }
  );
  return data;
}

export async function getMailchimpCampaigns(
  limit: number = 10,
  opts?: FetchOptions
): Promise<MailchimpCampaigns> {
  const { data } = await apiClient.get<MailchimpCampaigns>(
    `${BASE}/campaigns/`,
    { params: buildParams({ limit }, opts) }
  );
  return data;
}
